const minWidth = {
    name: "min-width",
    path: "/min-width",
    title: "min-width",
    description: "Utilities for controlling the minimum width of elements.",
    value: {
        "min-width": {
            "min-w-<number>": "calc(var(--1) * <number>)",
            "min-w-<fraction>": "calc(<fraction> * 100%)",
            "min-w-3xs": "var(--container-3xs); /* 16rem (256px) */",
            "min-w-2xs": "var(--container-2xs); /* 18rem (288px) */",
            "min-w-xs": "var(--container-xs); /* 20rem (320px) */",
            "min-w-sm": "var(--container-sm); /* 24rem (384px) */",
            "min-w-md": "var(--container-md); /* 28rem (448px) */",
            "min-w-lg": "var(--container-lg); /* 32rem (512px) */",
            "min-w-xl": "var(--container-xl); /* 36rem (576px) */",
            "min-w-2xl": "var(--container-2xl); /* 42rem (672px) */",
            "min-w-3xl": "var(--container-3xl); /* 48rem (768px) */",
            "min-w-4xl": "var(--container-4xl); /* 56rem (896px) */",
            "min-w-5xl": "var(--container-5xl); /* 64rem (1024px) */",
            "min-w-6xl": "var(--container-6xl); /* 72rem (1152px) */",
            "min-w-7xl": "var(--container-7xl); /* 80rem (1280px) */",
            "min-w-auto": "auto",
            "min-w-px": "1px",
            "min-w-full": "100%",
            "min-w-screen": "100vw",
            "min-w-dvw": "100dvw",
            "min-w-dvh": "100dvh",
            "min-w-lvw": "100lvw",
            "min-w-lvh": "100lvh",
            "min-w-svw": "100svw",
            "min-w-svh": "100svh",
            "min-w-min": "min-content",
            "min-w-max": "max-content",
            "min-w-fit": "fit-content",
            "min-w-[<value>]": "<value>",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="min-w-96 bg-color-blue-500 rounded-lg p-2 text-center">min-w-96</div>
<div class="min-w-80 bg-color-blue-500 rounded-lg p-2 text-center">min-w-80</div>
<div class="min-w-64 bg-color-blue-500 rounded-lg p-2 text-center">min-w-64</div>
<div class="min-w-48 bg-color-blue-500 rounded-lg p-2 text-center">min-w-48</div>
<div class="min-w-40 bg-color-blue-500 rounded-lg p-2 text-center">min-w-40</div>
<div class="min-w-32 bg-color-blue-500 rounded-lg p-2 text-center">min-w-32</div>
<div class="min-w-24 bg-color-blue-500 rounded-lg p-2 text-center">min-w-24</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="flex ...">
  <div class="min-w-3/4 ...">min-w-3/4</div>
  <div class="w-full ...">w-full</div>
</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="min-w-xl bg-color-blue-500 rounded-lg p-2 text-center">min-w-xl</div>
<div class="min-w-lg bg-color-blue-500 rounded-lg p-2 text-center">min-w-lg</div>
<div class="min-w-md bg-color-blue-500 rounded-lg p-2 text-center">min-w-md</div>
<div class="min-w-sm bg-color-blue-500 rounded-lg p-2 text-center">min-w-sm</div>
<div class="min-w-xs bg-color-blue-500 rounded-lg p-2 text-center">min-w-xs</div>
<div class="min-w-2xs bg-color-blue-500 rounded-lg p-2 text-center">min-w-2xs</div>
<div class="min-w-3xs bg-color-blue-500 rounded-lg p-2 text-center">min-w-3xs</div>
                `
            }
        },
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">min-w-&lt;number&gt;</code> utilities to set fixed minimum width values:
            </p>
            <div class="space-y-4 justify-items-start text-color-white font-mono font-bold relative w-60 mx-auto">
                <div class="absolute inset-0 z-0 w-20 !mb-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="relative min-w-96 bg-color-blue-500 rounded-lg p-2 text-center">min-w-96</div>
                <div class="relative min-w-80 bg-color-blue-500 rounded-lg p-2 text-center">min-w-80</div>
                <div class="relative min-w-64 bg-color-blue-500 rounded-lg p-2 text-center">min-w-64</div>
                <div class="relative min-w-48 bg-color-blue-500 rounded-lg p-2 text-center">min-w-48</div>
                <div class="relative min-w-40 bg-color-blue-500 rounded-lg p-2 text-center">min-w-40</div>
                <div class="relative min-w-32 bg-color-blue-500 rounded-lg p-2 text-center">min-w-32</div>
                <div class="relative min-w-24 bg-color-blue-500 rounded-lg p-2 text-center">min-w-24</div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">min-w-full, min-w-&lt;fraction&gt;</code>
                utilities like min-w-1/2 and min-w-2/5 to give an element a percentage-based minimum width:
            </p>
            <div class="space-y-4">
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="min-w-3/4 bg-color-blue-500 rounded-lg p-2 text-center">min-w-3/4</div>
                    <div class="w-full bg-color-blue-500 rounded-lg p-2 text-center">w-full</div>
                </div>
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="min-w-full bg-color-blue-500 rounded-lg p-2 text-center">min-w-full</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                <code class="text-color-gray-800 dark:text-color-white font-semibold">Using the container scale</code>
                Use utilities like min-w-sm and min-w-xl to set an element to a fixed minimum width based on the container scale:
            </p>
            <div class="space-y-4 justify-items-start text-color-white font-mono font-bold">
                <div class="min-w-xl bg-color-blue-500 rounded-lg p-2 text-center">min-w-xl</div>
                <div class="min-w-lg bg-color-blue-500 rounded-lg p-2 text-center">min-w-lg</div>
                <div class="min-w-md bg-color-blue-500 rounded-lg p-2 text-center">min-w-md</div>
                <div class="min-w-sm bg-color-blue-500 rounded-lg p-2 text-center">min-w-sm</div>
                <div class="min-w-xs bg-color-blue-500 rounded-lg p-2 text-center">min-w-xs</div>
                <div class="min-w-2xs bg-color-blue-500 rounded-lg p-2 text-center">min-w-2xs</div>
                <div class="min-w-3xs bg-color-blue-500 rounded-lg p-2 text-center">min-w-3xs</div>
                <div class="min-w-[100px] bg-color-blue-500 rounded-lg p-2 text-center">min-w-[100px]</div>
            </div>
            `
        },
    ]
};
export default minWidth;

<div className="sr-only min-w-xl min-w-[100px] min-w-lg min-w-md min-w-sm min-w-xs min-w-2xs min-w-3xs  min-w-full min-w-3/4 w-full !mb-0 space-y-4 w-60 w-20 min-w-96 min-w-80 min-w-64 min-w-48 min-w-40 min-w-32 min-w-24">

</div>